import React from 'react';
import { Divider, StackLayout } from '@leagueplatform/genesis-core';
import {
  TeamChallengeRank,
  UserChallengeRank,
} from '@leagueplatform/health-journey-api';
import { useIntl } from '@leagueplatform/locales';
import { useChallengesAnalytics } from '#hooks/use-challenges-analytics.hook.tsx';
import { EVENT_NAME, SCREEN_NAMES } from '@leagueplatform/analytics';
import { CLP_DETAIL, TAB_NAME } from '#constants/analytics.constants.tsx';
import { useGetUserProfile } from '#hooks/use-get-user-profile.hook.ts';
import { LoadMoreCTA } from '#components/leaderboard/load-more-cta/load-more-cta.component.tsx';
import { LeaderboardView } from '#pages/challenge-landing-page/components/leaderboard/hooks/use-get-leaderboard-data.hook.ts';
import { LeaderboardSkeletonLoader } from './leaderboard-skeleton-loader.component';
import { getDisplayedRankDetails } from './utils/get-displayed-rank-details.util';
import { LeaderboardRank } from './leaderboard-rank';

type LeaderboardListProps = {
  leaderboardData: (UserChallengeRank | TeamChallengeRank)[];
  displayViewMore?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage: () => void;
  leaderboardView: LeaderboardView;
  'aria-labelledby'?: string;
};

export function LeaderboardList({
  leaderboardData,
  displayViewMore,
  isFetchingNextPage,
  fetchNextPage,
  leaderboardView,
  'aria-labelledby': ariaLabelledBy,
}: LeaderboardListProps) {
  const { formatMessage } = useIntl();
  const { data: userData, isLoading: isUserLoading } = useGetUserProfile();
  const { trackChallengesAnalyticsEvent } = useChallengesAnalytics();

  if (isUserLoading) {
    return <LeaderboardSkeletonLoader />;
  }

  const handleAnalyticsClick = () => {
    trackChallengesAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      screen_name: SCREEN_NAMES.HEALTH_CAMPAIGN_DETAIL,
      detail: CLP_DETAIL.VIEW_MORE,
      tab_name: TAB_NAME.LEADERBOARD,
      challenge_leaderboard_type: leaderboardView,
    });
  };

  return (
    <>
      <Divider orientation="horizontal" css={{ marginY: '$none' }} />
      <StackLayout
        aria-labelledby={ariaLabelledBy}
        orientation="vertical"
        horizontalAlignment="stretch"
        spacing="$none"
        as="ol"
        divider={
          <Divider orientation="horizontal" css={{ marginY: '$none' }} />
        }
      >
        {leaderboardData.map((leaderboardRank) => {
          const {
            id,
            avatarUrl,
            currentUserOrTeamName,
            formattedName,
            isHighlightedRank,
            rank,
            score,
            rankingUnit,
            initials,
            teamNameInitial,
          } = getDisplayedRankDetails(
            leaderboardRank,
            // TODO remove the "!" once we actually check the `isSuccess` of the query
            userData!,
            formatMessage,
          );

          return (
            <LeaderboardRank
              key={id}
              rank={rank}
              rankingUnit={rankingUnit}
              avatarUrl={avatarUrl}
              initials={initials || teamNameInitial}
              score={score}
              highlight={isHighlightedRank}
              name={isHighlightedRank ? currentUserOrTeamName : formattedName}
            />
          );
        })}
      </StackLayout>

      {displayViewMore && (
        <LoadMoreCTA
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          onClick={handleAnalyticsClick}
        />
      )}
    </>
  );
}
